import { ReactNode, useEffect, useRef, useState } from 'react'
import { AppBar, Box, SwipeableDrawer, Toolbar, useMediaQuery, useTheme } from '@mui/material'
import { styled } from '@mui/styles'
import { grey } from '@mui/material/colors'
import { setPageToolbarIsFixed } from '../redux/slices/components'
import { useDispatch } from 'react-redux'

const Puller = styled('div')(() => ({
  width: 42,
  height: 4,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
  cursor: 'pointer'
}))

interface PageToolbarProps {
  children?: ReactNode | undefined
  drawer?: ReactNode | undefined
}

export default function PageToolbar({ children, drawer }: PageToolbarProps) {
  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * Indicates if the toolbar should be fixed.
   */
  const [isFixed, setIsFixed] = useState<boolean>(false)

  /**
   * Indicates if the screen is mobile.
   */
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  /**
   * The reference to anchorRef wrapper.
   */
  const anchorRef = useRef<HTMLDivElement | null>(null)

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The bleeding of the drawer.
   */
  const DRAWER_BLEEDING = 56

  /**
   * Get the width of the scroll bar.
   *
   * @see https://stackoverflow.com/a/13382873
   */
  function getScrollbarWidth() {
    // Creating invisible container
    const outer = document.createElement('div')
    outer.style.visibility = 'hidden'
    outer.style.overflow = 'scroll' // forcing scrollbar to appear
    // outer.style.msOverflowStyle = 'scrollbar' // needed for WinJS apps

    document.body.appendChild(outer)

    // Creating inner element and placing it in the container
    const inner = document.createElement('div')
    outer.appendChild(inner)

    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth

    if (outer.parentNode !== null) {
      // Removing temporary elements from the DOM
      outer.parentNode.removeChild(outer)
    }

    return scrollbarWidth
  }

  /**
   * Handle scroll to the root element.
   */
  function handleScroll(): void {
    // Check if the date range bar is out of bounds.
    const isToolbarOutOfBounds = (anchorRef?.current?.getBoundingClientRect()?.top ?? 0) < 0

    setIsFixed(isToolbarOutOfBounds)
  }

  //
  const [isSwipeableDrawerOpen, setIsSwipeableDrawerOpen] = useState<boolean>(false)

  const openSwipeableDrawer = () => {
    setIsSwipeableDrawerOpen(true)
  }

  const closeSwipeableDrawer = () => {
    setIsSwipeableDrawerOpen(false)
  }

  const toggleSwipeableDrawer = () => {
    setIsSwipeableDrawerOpen(!isSwipeableDrawerOpen)
  }

  // Runs when the component mounts.
  useEffect(() => {
    const main = document.getElementById('main')

    if (main) {
      main.addEventListener('scroll', handleScroll)

      // Check if the date range bar is out of bounds.
      const isToolbarOutOfBounds = (anchorRef?.current?.getBoundingClientRect()?.top ?? 0) < 0

      if (isToolbarOutOfBounds) {
        setIsFixed(true)
      }

      return () => main.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    dispatch(setPageToolbarIsFixed(isFixed))

    return () => {
      dispatch(setPageToolbarIsFixed(false))
    }
  }, [isFixed])

  return (
    <>
      {isMobile ? (
        <>
          {drawer ? (
            <SwipeableDrawer
              anchor="bottom"
              swipeAreaWidth={DRAWER_BLEEDING}
              disableSwipeToOpen={false}
              allowSwipeInChildren={true}
              ModalProps={{
                keepMounted: true
              }}
              PaperProps={{
                overflow: 'visible'
              }}
              sx={{
                '& .MuiPaper-root': {
                  overflow: 'visible',
                  boxShadow: 1,
                  right: `${getScrollbarWidth()}px`,
                  backgroundColor: theme.palette.background.paper
                }
              }}
              open={isSwipeableDrawerOpen}
              onClose={closeSwipeableDrawer}
              onOpen={openSwipeableDrawer}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: -DRAWER_BLEEDING,
                  borderTopLeftRadius: 8,
                  borderTopRightRadius: 8,
                  visibility: 'visible',
                  height: DRAWER_BLEEDING,
                  left: 0,
                  right: 0,
                  px: 6,
                  boxShadow:
                    '0px -2px 1px -1px rgba(0,0,0,0.2), 0px -1px 1px 0px rgba(0,0,0,0.14), 0px -1px 3px 0px rgba(0,0,0,0.12)',
                  backgroundColor: theme.palette.background.paper
                }}
              >
                <Puller onClick={toggleSwipeableDrawer} />
                {children}
              </Box>
              <Box
                sx={{
                  p: 6,
                  height: '100%',
                  overflow: 'auto',
                  backgroundColor: theme.palette.background.paper,
                  borderTop: `1px solid ${theme.palette.divider}`
                }}
              >
                {drawer}
              </Box>
            </SwipeableDrawer>
          ) : (
            <AppBar
              position="fixed"
              enableColorOnDark
              sx={{
                top: 'auto',
                bottom: 0,
                left: 0,
                right: `${getScrollbarWidth()}px`,
                padding: '0.75rem 1.5rem 0.75rem 0.25rem',
                overflowX: 'auto',
                width: 'auto',
                boxShadow: 1,
                background: theme.palette.background.paper
              }}
            >
              <Toolbar sx={{ p: '0 !important', minHeight: '0 !important' }}>{children}</Toolbar>
            </AppBar>
          )}
        </>
      ) : (
        isFixed && (
          <AppBar
            position="fixed"
            enableColorOnDark
            sx={{
              top: '64px',
              bottom: 'auto',
              left: '258px',
              right: `${getScrollbarWidth()}px`,
              overflowX: 'auto',
              width: 'auto',
              boxShadow: 1,
              background: theme.palette.background.paper
            }}
          >
            <Toolbar>{children}</Toolbar>
          </AppBar>
        )
      )}

      {!isMobile && (
        <div ref={anchorRef}>
          <Toolbar sx={{ p: '0 !important', minHeight: '0 !important' }}>{children}</Toolbar>
        </div>
      )}
    </>
  )
}
